<template>
  <NuxtLayout name="custom">
    <AM3UnlockPrompt
      v-if="!isCustomerProfileFeatureEnabled"
      title="Unlock Customer Profiles"
      :subtitle=" isCustomerProfileFeatureEnabled ? `Get closer to your audience by getting to know your top fans. <a href='https://audiencerepublic.com/features/audience-crm ' target='_blank' style='color: #FFF;'>Learn more</a>` : `Get to know your top fans. <a href='https://audiencerepublic.com/features/audience-crm ' target='_blank' style='color: #FFF;'>Learn more</a>`"
      :button-text="`Upgrade Plan`"
      button-icon-left="rocket-launch"
      @click="goToPlans"
    />

    <div
      class="am3-container"
      data-test-id="profile-container"
    >
      <AM3Hero
        :return-link="{
          path: heroReturnPath,
          text: heroReturnText,
        }"
      >
        <template #left>
          <div
            v-if="!imageUrl || imageUrl === undefined"
            class="max-w-[120px] sm:w-auto mr-4 md:mr-8"
          >
            <AM3Avatar
              :name="customerFormattedFullName || ''"
              size="xl"
            />
          </div>
          <!-- <div v-else-if="imageUrl">
            <img
              :src="imageUrl"
              :alt="displayedTitle || 'hero image'"
              class="w-64 rounded-md"
            >
          </div> -->
        </template>
        <template #center>
          <AM3LoadingContent
            v-if="customerState.isFetchingCustomer"
            class=" w-64 opacity-50 mb-3 [&>div>div]:h-10"
          />
          <h1
            v-else
            :class="customerFormattedFullName.length >= 48 ? 'text-lg sm:text-2xl' : 'text-2xl sm:text-4xl'"
            data-test-id="am3hero-hero-title"
            class="mb-3"
          >
            {{ customerFormattedFullName }}
          </h1>

          <AM3LoadingContent
            v-if="customerState.isFetchingCustomer"
            class="w-64 opacity-50"
          />
          <div
            v-else
            class="flex flex-row items-center text-xs gap-2 font-medium text-inverse opacity-75"
          >
            <p
              v-if="customerAge"
              class="flex flex-row items-center text-xs"
            >
              <AM3Icon
                size="sm"
                type="user"
                class="mr-2"
              />
              {{ customerAge }}
            </p>
            <p
              v-if="customerFormattedLocation"
              class="flex flex-row items-center text-xs"
            >
              <AM3Icon
                size="sm"
                type="map-pin"
                class="mr-2"
              />
              {{ customerFormattedLocation }}
            </p>
          </div>
        </template>
      </AM3Hero>
    </div>

    <!-- Navigation Tabs -->

    <AM3Tabs
      v-if="customerState.customer"
      :key="`tabs-${route.params.customer}`"
      v-model="activeTab"
      :tabs="navigationTabs"
      data-test-id="customer-profile-tabs"
      :enable-sticky="true"
      sticky-classes="border-b border-solid border-surface-dark bg-surface"
      sync-with-url
    />

    <!-- Tab Content -->
    <div
      v-if="customerState.customer"
      v-paywall="paywallOptions.showPaywall ? true : paywallOptions "
      class="tab-content mt-4 relative am3-container pb-24"
      :class="paywallOptions.showPaywall === false ? 'relative h-[600px] overflow-hddien border border-solid border-weak' : '' "
    >
      <!-- Overview Tab -->
      <div
        v-if="activeTab === 'overview'"
        class="space-y-6"
      >
        <CustomerOverview />
      </div>

      <!-- Purchases Tab -->
      <div
        v-else-if="(customerState.customer) && activeTab === 'purchases'"
        class="tab-content mt-4 relative space-y-6"
        data-test-id="profiles-tab-content"
      >
        <CustomerPurchases />
      </div>

      <!-- Events Tab -->
      <div
        v-else-if="activeTab === 'events'"
        class="space-y-6"
      >
        <CustomerEvents />
      </div>

      <!-- Data Capture Tab -->
      <div
        v-else-if="activeTab === 'data-capture'"
        class="space-y-6"
      >
        <CustomerDataCapture />
      </div>

      <!-- Activity Tab -->
      <div
        v-else-if="activeTab === 'activity'"
        class="space-y-6"
      >
        <CustomerActivity />
      </div>
    </div>
  </NuxtLayout>
</template>

<script lang="ts" setup>
import { computed, shallowRef, ref, onMounted, onBeforeUnmount, watch, defineAsyncComponent } from 'vue'
import { useStore } from 'vuex'
import { useRoute, useRouter } from 'vue-router'
import { countriesWithStateSelectors, countryList } from '@/utils/countries'
import { getTruncatedString } from '@/utils/string'
import AM3UnlockPrompt from '@/components/am3/prompts/AM3UnlockPrompt.vue'
import AM3Hero from '@/components/am3/hero/AM3Hero.vue'
import AM3HeroImage from '@/components/am3/hero/AM3HeroImage.vue'
import AM3Tabs from '@/components/am3-generic/layout/AM3Tabs.vue'
import CustomerOverview from '@/pages/audience/[customer]/view/components/overview.vue'
import CustomerPurchases from '@/pages/audience/[customer]/view/components/purchases.vue'
import CustomerEvents from '@/pages/audience/[customer]/view/components/events.vue'
import CustomerDataCapture from '@/pages/audience/[customer]/view/components/data-capture.vue'
import CustomerActivity from '@/pages/audience/[customer]/view/components/activity.vue'

import { formatCustomerAge } from '@/store/modules/customer/utils'

const store = useStore()
const route = useRoute()
const router = useRouter()

// Reactive state for tab management
const activeTab = ref('overview')

const heroReturnText = shallowRef('Back to contacts')
const heroReturnPath = shallowRef('/audience')

// Getters
const isFeatureEnabled = features => store.getters['authModule/isFeatureEnabled'](features)
const historicalPreviousPagePath = defaultPath => store.getters['authModule/historicalPreviousPagePath'](defaultPath)
const isPermissionEnabled = permissions => store.getters['authModule/isPermissionEnabled'](permissions)

// State
const customerState = computed(() => store.state.customerModule)
const fanLoyaltyMemberships = computed(() => store.state.customerModule.fanLoyaltyMemberships)
const loyaltyProgramTiersMap = computed(() => store.state.customerModule.loyaltyProgramTiersMap)
const customerCustomFields = computed(() => store.state.customerModule.customerCustomFields)

// Computed properties with proper memoization
const currentPath = computed(() => {
  return route.path.split('/').filter(Boolean).pop() // Get the last segment of the path
})

// Navigation tabs configuration
const navigationTabs = computed(() => {
  const customerId = route.params.customer
  const tabs = [
    { key: 'overview', title: 'Overview' },
  ]

  if (isPermissionEnabled(['view-sales'])) {
    tabs.push(
      { key: 'purchases', title: 'Orders' },
      { key: 'events', title: 'Events' },
    )
  }
  else {
    tabs.push({ key: 'events', title: 'Events' })
  }

  tabs.push(
    { key: 'data-capture', title: 'Data capture' },
    { key: 'activity', title: 'Activity' },
  )

  return tabs
})

const isCustomerProfileFeatureEnabled = computed(() => {
  return isFeatureEnabled(['audience', 'customer-profiles'])
})

const paywallOptions = ref({
  showLockIcon: true,
  showPaywall: isCustomerProfileFeatureEnabled,
  title: 'Unlock customer profiles',
  description: 'Get closer to your audience by getting to know your top fans..',
  buttonText: 'Upgrade Plan',
  buttonIcon: 'rocket',
})

const customerFormattedFullName = computed(() => {
  console.log(customerState.value.customer)
  const customer = customerState.value.customer
  if (!customer) return ''

  if (!customer.firstName && !customer.lastName) {
    return customer.emailAddress || ''
  }
  if (!customer.lastName) {
    return customer.firstName
  }

  const combined = `${customer.firstName} ${customer.lastName}`

  if (combined.length >= 48) {
    return getTruncatedString(combined, 48) + ' ...'
  }
  else {
    return combined
  }
})

const customerAge = computed((): string | null => {
  const customer = customerState.value.customer
  if (!customer) return null
  return formatCustomerAge(customer)
})

useHead({
  title: computed(() => {
    return `Profile - ${customerFormattedFullName.value || customerState.value.customer?.name || ''}`
  }),

})

const customerFormattedLocation = computed(() => {
  const customer = customerState.value.customer
  if (!customer) return ''

  const { city, state, country } = customer
  const locationParts = []

  if (city) locationParts.push(city)
  if (state) locationParts.push(state.toUpperCase())

  const relevantCountry = countryList.find(item => item.iso === country)
  if (relevantCountry) locationParts.push(relevantCountry.name)

  return locationParts.join(', ')
})

// Methods
function getLoyaltyTierTitle(loyaltyMembership) {
  const loyaltyTier = loyaltyProgramTiersMap.value[loyaltyMembership.fanLoyaltyTier.loyaltyTierOid]
  switch (loyaltyMembership.provider) {
    case 'patreon':
      return `${customerState.value.customer.name} is a Patreon member — ${loyaltyTier ? loyaltyTier.title : 'Custom pledge'}`
    default:
      return null
  }
}

function hasStateSelect(countyIsoCode) {
  if (!countyIsoCode) return false
  return countriesWithStateSelectors.indexOf(countyIsoCode) > -1
}

async function goToPlans() {
  window.open('https://www.audiencerepublic.com/book-a-demo', '_blank')
}

// Fetch the fan matching the route's oid.
// If no fan matching this oid can be found, lets take the user back to the audience page
async function fetchFan() {
  if (customerState.value.isFetchingCustomer) {
    return
  }

  // store.commit('customerModule/RESET_CUSTOMERS')
  await store.dispatch('customerModule/FETCH_CUSTOMER', { fanOid: route.params.customer })

  if (!customerState.value.customer) {
    router.push({
      path: `/audience`,
    })
  }
}

async function fetchCustomFields() {
  if (customerState.value.isFetchingCustomerCustomFields) {
    return
  }

  await store.dispatch('customerModule/FETCH_CUSTOMER_CUSTOM_FIELDS', { fanOid: route.params.customer })
  await store.dispatch('customFieldModule/FETCH_CUSTOM_FIELDS', { type: 'profile' })
}

// Lifecycle hooks
onMounted(async () => {
  try {
    const eventReferrerUrl = sessionStorage.getItem('profile_event_referrer_url')
    if (eventReferrerUrl && eventReferrerUrl.includes('/events/')) {
      heroReturnText.value = 'Back to event'
      heroReturnPath.value = eventReferrerUrl
    }
    else {
      const savedAudienceUrl = localStorage.getItem('audience_referrer_url')
      if (savedAudienceUrl && savedAudienceUrl.startsWith('/audience')) {
        heroReturnPath.value = savedAudienceUrl
      }
    }
  }
  catch (e) {
    console.warn('Could not access referrer URL:', e)
  }

  // store.commit('customerModule/RESET_CUSTOMER_DATA')
  await store.dispatch('authModule/FETCH_PERMISSIONS')
  await fetchFan()
  fetchCustomFields()
})

onBeforeUnmount(() => {
  // Clear all customer data when leaving the profile entirely
  store.commit('customerModule/RESET_CUSTOMER_DATA')
  store.commit('customerModule/RESET_CUSTOMER_ACTIVITY')
  store.commit('customerModule/RESET_CUSTOMER_PURCHASES')
  store.commit('customerModule/RESET_CUSTOMER_EVENTS')
  store.commit('customerModule/RESET_CUSTOMER_CAMPAIGNS')
})

// check if create does the same - is it reloading?

// Watch for customer ID changes to reload data
watch(() => route.params.customer, (newId, oldId) => {
  if (newId !== oldId) {
    // Clear all previous customer data before loading new customer
    store.commit('customerModule/RESET_CUSTOMER_DATA')
    store.commit('customerModule/RESET_CUSTOMER_ACTIVITY')
    store.commit('customerModule/RESET_CUSTOMER_PURCHASES')
    store.commit('customerModule/RESET_CUSTOMER_EVENTS')
    store.commit('customerModule/RESET_CUSTOMER_CAMPAIGNS')

    fetchFan()
    fetchCustomFields()
  }
}, { immediate: false })
</script>

<style scoped>
.content {
  padding: 30px;
}
</style>
